import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Award, Upload } from "lucide-react";
import api, { formatApiError, API } from "../../lib/api";
import { inputCls, labelCls } from "./shared";

export default function CertificadoTab() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [ts, setTs] = useState(Date.now());

  const load = useCallback(() => {
    api.get("/certificate-layout")
      .then((r) => { setCfg(r.data); setTs(Date.now()); })
      .catch((e) => toast.error(formatApiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/certificate-layout", fd);
      toast.success("Layout do certificado atualizado!");
      load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const saveCfg = async () => {
    try {
      await api.put("/admin/certificate-layout/config", {
        name_y: Number(cfg.name_y),
        font_size: Number(cfg.font_size),
        color: cfg.color,
        show_event_line: cfg.show_event_line,
        show_code: cfg.show_code,
      });
      toast.success("Configurações do certificado salvas");
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const removeLayout = async () => {
    if (!window.confirm("Remover o layout personalizado e voltar ao padrão da marca?")) return;
    await api.delete("/admin/certificate-layout");
    toast.success("Layout removido. O certificado voltou ao padrão da marca.");
    load();
  };

  if (!cfg) return <p className="text-slate-400 text-sm">Carregando...</p>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <div className="card-metric space-y-4 h-fit">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2">
          <Award size={18} className="text-[#F5A623]" /> Layout do certificado
        </h3>
        <p className="text-xs text-slate-400 leading-relaxed">
          Envie a arte do certificado (PNG ou JPG, orientação paisagem, ex.: 3508×2480 px).
          O nome do participante, os dados do evento e o código de verificação são sobrepostos
          automaticamente. Sem layout, usamos o modelo padrão da marca.
        </p>
        {cfg.has_layout && (
          <p className="text-xs text-slate-400 font-data">Atual: {cfg.filename}</p>
        )}
        <label className="btn-secondary inline-flex items-center gap-2 cursor-pointer" data-testid="admin-upload-cert-layout">
          <Upload size={15} /> {busy ? "Enviando..." : cfg.has_layout ? "Trocar layout" : "Enviar layout"}
          <input type="file" accept=".png,.jpg,.jpeg" className="hidden" disabled={busy}
            onChange={(e) => upload(e.target.files[0])} />
        </label>
        {cfg.has_layout && (
          <button onClick={removeLayout} className="btn-ghost text-sm text-red-400 block" data-testid="cert-layout-remove">
            Remover layout (voltar ao padrão)
          </button>
        )}
        <div className="border-t border-[#00C8FF]/15 pt-4 space-y-4">
          <div>
            <label className={labelCls}>Posição vertical do nome ({cfg.name_y}%)</label>
            <input type="range" min="10" max="85" value={cfg.name_y}
              onChange={(e) => setCfg({ ...cfg, name_y: e.target.value })}
              className="w-full accent-[#F5A623]" data-testid="cert-layout-name-y" />
          </div>
          <div>
            <label className={labelCls}>Tamanho da fonte do nome</label>
            <input type="number" min="16" max="48" value={cfg.font_size}
              onChange={(e) => setCfg({ ...cfg, font_size: e.target.value })}
              className={inputCls} data-testid="cert-layout-font-size" />
          </div>
          <div>
            <label className={labelCls}>Cor do texto sobre a arte</label>
            <select value={cfg.color} onChange={(e) => setCfg({ ...cfg, color: e.target.value })}
              className={inputCls} data-testid="cert-layout-color">
              <option value="#FFFFFF">Branco</option>
              <option value="#0C1530">Azul-marinho (escuro)</option>
              <option value="#000000">Preto</option>
            </select>
          </div>
          <label className="flex items-center gap-2.5 text-sm text-slate-300 cursor-pointer">
            <input type="checkbox" checked={cfg.show_event_line}
              onChange={(e) => setCfg({ ...cfg, show_event_line: e.target.checked })}
              className="w-4 h-4 accent-[#F5A623]" data-testid="cert-layout-show-event" />
            Mostrar linha com evento, data e carga horária
          </label>
          <label className="flex items-center gap-2.5 text-sm text-slate-300 cursor-pointer">
            <input type="checkbox" checked={cfg.show_code}
              onChange={(e) => setCfg({ ...cfg, show_code: e.target.checked })}
              className="w-4 h-4 accent-[#F5A623]" data-testid="cert-layout-show-code" />
            Mostrar código de verificação
          </label>
          <button onClick={saveCfg} className="btn-primary" data-testid="cert-layout-save">Salvar configurações</button>
        </div>
      </div>
      <div className="card-metric">
        <h3 className="text-lg font-semibold text-white mb-4">Pré-visualização da arte</h3>
        {cfg.has_layout ? (
          <img src={`${API}/certificate-layout/image?t=${ts}`} alt="Layout do certificado"
            className="w-full rounded-lg border border-[#00C8FF]/25" data-testid="cert-layout-preview" />
        ) : (
          <p className="text-sm text-slate-500" data-testid="cert-layout-empty">
            Nenhum layout enviado — o certificado usa o modelo padrão da marca Pregoeiros Summit.
          </p>
        )}
      </div>
    </div>
  );
}
